import { useCallback, useEffect, useReducer, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { ToolResultBlock, ToolTurnPayload, ToolUseBlock } from '../domTools'

import {
  AssistantStreamEventType,
  useAssistantMessageStreamSubscription
} from '../../../generated/graphql'
import { usePageContextStore } from '../../../lib/context/PageContext'
import { buildMessage } from '../chatMessage'
import { buildHistory, HistoryEntry, makeToolTurnPair, withToolTurns } from './history'
import { INITIAL_STATE, streamingReducer } from './streamingReducer'
import useChatMessages from './useChatMessages'
import useExecuteDomTools from './useExecuteDomTools'
import useStepMessages from './useStepMessages'
import useStopProtocol from './useStopProtocol'

// Stable fallback passed to useSubscription when skip=true — avoids a new
// object reference on every render while the subscription is idle.
const SKIP_VARS = {
  message: '',
  history: [] as HistoryEntry[],
  pageContext: '',
  skill: null
}

const SEND_ERROR_MESSAGE = 'Something went wrong. Could you try again or rephrase the question?'

/**
 * Formats the assistant's page context string — the URL plus any extra text
 * the page registered via usePageContext. Sent to the backend with each turn.
 */
function buildPageContext(pathname: string, pageContext: string): string {
  return `URL: ${pathname}${pageContext ? `\n\n${pageContext}` : ''}`
}

// Runtime type guard for Anthropic ToolUseBlock. Filters parsed JSON before
// dispatch so a malformed payload missing id or name can't sneak through and
// produce a tool_result with `tool_use_id: undefined` later — the cast alone
// silently lies about the shape.
const isToolUseBlock = (v: unknown): v is ToolUseBlock => {
  if (typeof v !== 'object' || v === null) return false
  const b = v as Record<string, unknown>
  return b.type === 'tool_use' && typeof b.id === 'string' && typeof b.name === 'string'
}

/**
 * Manages the chat message history and subscription for the AI assistant.
 * Persists messages to sessionStorage; keeps streaming state locally.
 * Streams each response via the assistantMessageStream subscription — text arrives
 * word-by-word and tool status labels appear inline while tools execute.
 */
const useAssistantChat = () => {
  const [state, dispatch] = useReducer(streamingReducer, INITIAL_STATE)
  const {
    inProgressText,
    currentStatus,
    subscriptionVars,
    pendingCommit,
    pendingDomTools,
    pendingToolTurns
  } = state
  const loading = subscriptionVars !== null

  const { pathname } = useLocation()
  const [messages, setMessages] = useChatMessages()
  const { pageData: pageContext } = usePageContextStore()
  const isMountedRef = useRef(true)
  // Mirror of messages and pendingToolTurns read by appendAndResume. The
  // resume callback fires after a navigation settle delay (800ms+); without
  // refs, the closure captures messages from the render that registered it
  // and would build history from a stale snapshot.
  const messagesRef = useRef(messages)
  messagesRef.current = messages
  const pendingToolTurnsRef = useRef(pendingToolTurns)
  pendingToolTurnsRef.current = pendingToolTurns
  const pageContextRef = useRef(pageContext)
  pageContextRef.current = pageContext
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname
  const counterRef = useRef(
    messages.reduce((max, m) => {
      const n = parseInt(m.id.replace('msg-', ''), 10)
      return Number.isNaN(n) ? max : Math.max(max, n)
    }, 0)
  )

  // Memoized so the identity is stable across renders. nextId is consumed by
  // hooks (useStepMessages, layout effects) — if it ever lands in a dep array
  // the unmemoized form would re-trigger every render.
  const nextId = useCallback(() => {
    counterRef.current += 1
    return `msg-${counterRef.current}`
  }, [])

  // Owns cancelledRef + the layout effect that drains pendingCommit + the
  // stopGeneration handler. Returns cancelledRef so we can thread it to
  // useExecuteDomTools and reset it on every SEND/RESUME below.
  const { cancelledRef, stopGeneration, clearConversation } = useStopProtocol({
    inProgressText,
    pendingCommit,
    setMessages,
    dispatch,
    nextId
  })

  const appendAndResume = (toolUse: ToolUseBlock[], toolResults: ToolResultBlock[]) => {
    cancelledRef.current = false
    const newTurns: HistoryEntry[] = makeToolTurnPair(toolUse, toolResults)
    // Read from refs so the resume picks up the latest state after the
    // navigation settle delay, not the snapshot from the render that
    // registered this closure.
    dispatch({
      type: 'RESUME',
      newTurns,
      vars: {
        message: '',
        history: [
          ...buildHistory(messagesRef.current),
          ...pendingToolTurnsRef.current,
          ...newTurns
        ],
        pageContext: buildPageContext(pathnameRef.current, pageContextRef.current ?? '')
      }
    })
  }

  useEffect(
    () => () => {
      isMountedRef.current = false
    },
    []
  )

  const { pushStep, markStepDone, markStepError } = useStepMessages(setMessages, nextId)

  useExecuteDomTools({
    pendingDomTools,
    onStepStart: pushStep,
    onStepDone: markStepDone,
    onStepError: markStepError,
    onComplete: appendAndResume,
    onError: () => {
      if (!isMountedRef.current) return
      dispatch({ type: 'ERROR', content: SEND_ERROR_MESSAGE })
    },
    cancelledRef
  })

  useAssistantMessageStreamSubscription({
    skip: subscriptionVars === null,
    variables: subscriptionVars ?? SKIP_VARS,
    onError: () => {
      if (!isMountedRef.current) return
      dispatch({ type: 'ERROR', content: SEND_ERROR_MESSAGE })
    },
    onData: ({ data }) => {
      if (!isMountedRef.current) return
      const event = data.data?.assistantMessageStream
      if (!event) return

      switch (event.type) {
        case AssistantStreamEventType.Token:
          dispatch({ type: 'TOKEN', content: event.content ?? '' })
          break
        case AssistantStreamEventType.ToolStatus:
          dispatch({ type: 'TOOL_STATUS', content: event.content ?? null })
          break
        case AssistantStreamEventType.Done:
          dispatch({ type: 'DONE' })
          break
        case AssistantStreamEventType.Error:
          dispatch({ type: 'ERROR', content: event.content ?? SEND_ERROR_MESSAGE })
          break
        case AssistantStreamEventType.DomToolCall: {
          if (!event.content) return
          // A malformed payload throws synchronously inside Apollo's onData,
          // and Apollo swallows it without firing onError — the hook would
          // stall with loading=true forever and no visible error. Catch the
          // throw and surface it as a normal error event instead. Filter
          // through the type guard so a parsed-but-malformed array (e.g. a
          // block missing id) cannot corrupt the resume payload downstream.
          try {
            const parsed = JSON.parse(event.content) as unknown
            if (!Array.isArray(parsed)) {
              dispatch({ type: 'ERROR', content: SEND_ERROR_MESSAGE })
              break
            }
            const blocks = parsed.filter(isToolUseBlock)
            if (blocks.length !== parsed.length) {
              dispatch({ type: 'ERROR', content: SEND_ERROR_MESSAGE })
              break
            }
            dispatch({ type: 'DOM_TOOL_CALL', content: blocks })
          } catch {
            dispatch({ type: 'ERROR', content: SEND_ERROR_MESSAGE })
          }
          break
        }
        case AssistantStreamEventType.ToolTurn: {
          if (!event.content) return
          try {
            const { assistant, user } = JSON.parse(event.content) as ToolTurnPayload
            dispatch({ type: 'TOOL_TURN', pair: makeToolTurnPair(assistant, user) })
          } catch {
            dispatch({ type: 'ERROR', content: SEND_ERROR_MESSAGE })
          }
          break
        }
        default:
          break
      }
    }
  })

  const sendMessage = (text: string, skill?: string) => {
    if (loading) return
    cancelledRef.current = false
    // The SEND reducer action clears pendingToolTurns once they are folded in here.
    const history = withToolTurns(buildHistory(messages), pendingToolTurns)
    const contextToSend = buildPageContext(pathname, pageContext ?? '')
    setMessages((prev) => [...prev, buildMessage(nextId(), 'user', text, skill)])
    dispatch({
      type: 'SEND',
      vars: { message: text, history, pageContext: contextToSend, skill: skill ?? null }
    })
  }

  const clearHistory = () => {
    // Without the cancel, a DOM tool still running resumes into the emptied panel.
    clearConversation()
    setMessages([])
  }

  return {
    messages,
    loading,
    sendMessage,
    stopGeneration,
    clearHistory,
    inProgressText,
    currentStatus
  }
}

export default useAssistantChat
